import { gql } from '@/utilities/gql';

export const NARRATIVE_EDIT_BUTTON_FRAGMENT = gql(`
  fragment NarrativeEditButtonFields on Narrative @_unmask {
    id
    owner {
      publicId
    }
    companyId
    latestPublishedUpdate {
      id
      willLockAt
    }
    earliestPublishedUpdate {
      id
    }
  }
`);

export const NARRATIVE_CARD_ACTIONS_FRAGMENT = gql(
  `
  fragment NarrativeCardActionsFields on Narrative @_unmask {
    id
    isPrimary
    valuation {
      type
    }
    company {
      id
    }
    ...NarrativeEditButtonFields
  }
`,
  [NARRATIVE_EDIT_BUTTON_FRAGMENT]
);

export const NARRATIVE_URL_COMPANY_FRAGMENT = gql(`
  fragment NarrativeUrlCompanyFields on Company @_unmask {
    canonicalURL
  }
`);

export const NARRATIVE_OWNER_FRAGMENT = gql(`
  fragment NarrativeOwnerFields on CommunityProfile @_unmask {
    publicId
    displayName
    imageUrl
    role
    disclaimerType
  }
`);

// =========

export const NARRATIVE_CARD_SYNTHETIC_FRAGMENT = gql(
  `
  fragment NarrativeCardSyntheticFields on Narrative @_unmask {
    id
    title
    type
    slug
    primaryCount
    valuation {
      type
      fairValue
      intrinsicDiscount
    }
    company {
      id
      tickerSymbol
      name
      canonicalURL
      data {
        currencyInfo
      }
      info {
        url
      }
      analysisValue {
        priceTargetAnalystCount
      }
    }
    ...NarrativeCardActionsFields
  }
`,
  [NARRATIVE_CARD_ACTIONS_FRAGMENT]
);

export const NARRATIVE_CARD_FRAGMENT = gql(
  `
  fragment NarrativeCardFields on Narrative @_unmask {
    id
    slug
    updatedAt
    primaryCount
    commentCount
    title
    isPublic
    excerpt
    type
    isSponsored
    lastFeaturedAt
    company {
      id
      tickerSymbol
      name
      data {
        currencyInfo
      }
      analysisValue {
        priceTargetAnalystCount
      }
      info {
        url
        hasLogo
      }
      ...NarrativeUrlCompanyFields
    }
    owner {
      ...NarrativeOwnerFields
    }
    valuation {
      fairValue
      intrinsicDiscount
      revenuePercentageChange
    }
    voteCount {
      upvotes
    }
    draftUpdate {
      title
      coreExcerpt
      valuation {
        fairValue
        revenuePercentageChange
        intrinsicDiscount
      }
      updatedAt
    }
    earliestPublishedUpdate {
      willLockAt
    }
    latestPublishedUpdate {
      willLockAt
      coreExcerpt
      title
    }
    ...NarrativeCardActionsFields
  }
`,
  [
    NARRATIVE_URL_COMPANY_FRAGMENT,
    NARRATIVE_OWNER_FRAGMENT,
    NARRATIVE_CARD_ACTIONS_FRAGMENT,
  ]
);

export const NARRATIVE_CARD_ISOMORPHIC_FRAGMENT = gql(
  `
  fragment NarrativeCardIsomorphicFields on Narrative @_unmask {
    ...NarrativeCardFields
    ...NarrativeCardSyntheticFields
  }
`,
  [NARRATIVE_CARD_FRAGMENT, NARRATIVE_CARD_SYNTHETIC_FRAGMENT]
);

export const FAIR_VALUE_HISTOGRAM_COMPANY_FRAGMENT = gql(`
  fragment FairValueHistogramCompanyFields on Company @_unmask {
    tickerSymbol
    data {
      currencyInfo
    }
    myValuation {
      fairValue
    }
    analysisValue {
      lastSharePrice
    }
    valuationHistogram {
      primaryCount
      min
      max
      buckets {
        min
        max
        primaryCount
      }
    }
  }
`);

export const COMPANY_REPORT_NARRATIVE_CARD_FRAGMENT = gql(
  `
  fragment CompanyReportNarrativeCardFields on Narrative @_unmask {
    id
    company {
      id
      uniqueSymbol
    }
    ...NarrativeCardIsomorphicFields
  }
`,
  [NARRATIVE_CARD_ISOMORPHIC_FRAGMENT]
);

export const FAIR_VALUE_HISTORY_GRAPH_NARRATIVE_FRAGMENT = gql(`
  fragment FairValueHistoryGraphNarrativeFields on Narrative @_unmask {
    companyId
    updatedAt
    valuation {
      fairValue
    }
    valuations {
      fairValue
      createdAt
    }
    owner {
      displayName
    }
    company {
      analysisValue {
        lastSharePrice
        return1yAbs
        return7d
        npvPerShare
      }
      data {
        currencyInfo
      }
    }
  }
`);

export const FISCAL_DATA_FRAGMENT = gql(`
  fragment FiscalDataFields on Valuation @_unmask {
    fiscalData {
      mostRecentReportedDate
      yearlyTimeSeries {
        year
        data {
          netIncome
          revenue
          endDate
          totalSharesOutstanding
        }
      }
    }
  }
`);

export const VALUATION_FRAGMENT = gql(
  `
  fragment ValuationFields on Valuation @_unmask {
      exchangeRate
      discountRate
      fairValue
      futurePePercentageChange
      profitMarginPercentageChange
      revenuePercentageChange
      timeframe
      sharesOutstandingGrowthRate
      ...FiscalDataFields
  }
`,
  [FISCAL_DATA_FRAGMENT]
);

export const NARRATIVE_FRAGMENT = gql(
  `
  fragment NarrativeFields on Narrative @_unmask {
    excerpt
    isDiscoverable
    companyId
    primaryCount
    draftUpdate {
      contentHtml
      title
      valuation {
        ...ValuationFields
      }
    }
    latestPublishedUpdate {
      coreHtml
    }
    earliestPublishedUpdate {
      willLockAt
    }
    isSponsored
    hasHadSponsorship
    externalLink
    contentBlocks {
      title
      id
      contentHtml
    }
    id
    slug
    title
    isPrimary
    valuation {
      ...ValuationFields
    }
    voteCount {
      upvotes
    }
    userHoldsPosition
    coverImageUrl
    createdAt
    updatedAt
    type
    owner {
      ...NarrativeOwnerFields
    }
    company {
      uniqueSymbolSlug
      country {
        iso2
      }
      primaryIndustry {
        slug
      }
      info {
        hasLogo
        url
      }
      media {
        transform
        url
      }
    }
  }
`,
  [NARRATIVE_OWNER_FRAGMENT, VALUATION_FRAGMENT]
);
